import type { StoryboardSegmentPreviewEntry } from '../types';

const VIDEO_URL_FIELD_CANDIDATES = [
  'video_url',
  'videoUrl',
  'result_url',
  'resultUrl',
  'output_url',
  'outputUrl',
  'url',
];

const NESTED_VIDEO_CANDIDATE_KEYS = ['data', 'detail', 'response', 'result', 'output'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value.trim());

export const extractVideoUrlFromResult = (
  value: unknown,
  seen: Set<unknown> = new Set()
): string | null => {
  if (value == null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed && isHttpUrl(trimmed)) {
      return trimmed;
    }
    return null;
  }

  if (seen.has(value)) {
    return null;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractVideoUrlFromResult(item, seen);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  if (!isRecord(value)) {
    return null;
  }

  for (const key of VIDEO_URL_FIELD_CANDIDATES) {
    const raw = value[key];
    if (typeof raw === 'string' && raw.trim() && isHttpUrl(raw)) {
      return raw.trim();
    }
    if (Array.isArray(raw)) {
      for (const item of raw) {
        const nested = extractVideoUrlFromResult(item, seen);
        if (nested) {
          return nested;
        }
      }
    }
  }

  for (const key of NESTED_VIDEO_CANDIDATE_KEYS) {
    const nested = value[key];
    if (nested !== undefined) {
      const nestedUrl = extractVideoUrlFromResult(nested, seen);
      if (nestedUrl) {
        return nestedUrl;
      }
    }
  }

  const raw = value.raw;
  if (raw !== undefined) {
    const nested = extractVideoUrlFromResult(raw, seen);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const createPreviewId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `preview-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const createPreviewEntry = (
  segmentId: string,
  url?: string,
  extras?: Partial<StoryboardSegmentPreviewEntry>
): StoryboardSegmentPreviewEntry => ({
  previewId: createPreviewId(),
  segmentId,
  url,
  createdAt: Date.now(),
  ...extras,
});

export const resolvePreviewEntryDisplayUrl = (
  entry: StoryboardSegmentPreviewEntry | null | undefined
): string | undefined => {
  if (!entry) {
    return undefined;
  }
  if (entry.url && entry.url.trim()) {
    return entry.url;
  }
  const downloadSource = entry.downloadSource;
  if (downloadSource?.type === 'sora') {
    return `storyboard-sora://${downloadSource.videoId}`;
  }
  return undefined;
};

